/**
 * Collapsible image text section
 */
class CollapsibleImageText extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.sectionId = this.dataset.sectionId;
    // Block id of the currently open item, or null when everything is closed
    this.openBlockId = null;
    this.init();
  }

  init() {
    const self = this;
    const titles = this.querySelectorAll('.collapsible-title');
    titles.forEach(function (title) {
      title.addEventListener('click', self.handleTitleClick.bind(self));
    });
  }

  handleTitleClick(event) {
    // Use currentTarget so a click on a child (title text or the +/- glyph)
    // still resolves to the title element that carries the block id.
    const title = event.currentTarget;
    const blockId = title.dataset.blockId;
    if (!blockId) {
      return;
    }

    const content = this.querySelector('.collapsible-content[data-block-id="' + blockId + '"]');
    if (!content) {
      return;
    }

    const isOpen = !!content.style.maxHeight;

    // Close any other open item first (accordion behaviour)
    this.closeAll();

    if (isOpen) {
      // The clicked item was open, so a toggle closes it and leaves all closed
      this.openBlockId = null;
    } else {
      this.openContent(content, title);
      this.openBlockId = blockId;
    }

    this.updateTitleStatuses();
  }

  openContent(content, title) {
    content.style.maxHeight = content.scrollHeight + 'px';
    title.setAttribute('aria-expanded', 'true');
    const action = title.querySelector('.collapsible-action');
    if (action) {
      action.innerText = '-';
    }
  }

  closeContent(content, title) {
    content.style.maxHeight = null;
    title.setAttribute('aria-expanded', 'false');
    const action = title.querySelector('.collapsible-action');
    if (action) {
      action.innerText = '+';
    }
  }

  closeAll() {
    const self = this;
    const titles = this.querySelectorAll('.collapsible-title');
    titles.forEach(function (title) {
      const blockId = title.dataset.blockId;
      const content = self.querySelector('.collapsible-content[data-block-id="' + blockId + '"]');
      if (content && content.style.maxHeight) {
        self.closeContent(content, title);
      }
    });
  }

  updateTitleStatuses() {
    const self = this;
    const titles = this.querySelectorAll('.collapsible-title');
    titles.forEach(function (title) {
      // Dim non-open titles while an item is open; clear all dimming when
      // everything is closed.
      if (self.openBlockId && title.dataset.blockId !== self.openBlockId) {
        title.classList.add('inactive');
      } else {
        title.classList.remove('inactive');
      }
    });
  }
}

customElements.define('collapsible-image-text', CollapsibleImageText);

/**
 * Corporate gifting form
 */
class CorporateGiftingForm extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.sectionId = this.dataset.sectionId;
    this.openPopupButton = this.querySelector('.corporate-gifting-form-popup-button');
    this.corporateGiftingFormPopup = this.querySelector('.corporate-gifting-form-popup');
    this.corporateGiftingFormPopupCloseButton = this.querySelector('.corporate-gifting-form-popup-close-button');
    this.form = this.querySelector('form');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.formErrorMessages = {
      inputTextRequired: this.dataset.formInputTextErrorRequiredMessage,
      selectErrorRequired: this.dataset.formSelectErrorRequiredMessage,
      inputCheckboxRequired: this.dataset.formInputCheckboxErrorRequiredMessage,
      inputRadioRequired: this.dataset.formInputRadioErrorRequiredMessage
    };
    this.formFieldsSelector = {
      firstName: 'input[name="contact[first_name]"]',
      lastName: 'input[name="contact[last_name]"]',
      email: 'input[name="contact[email]"]',
      companyName: 'input[name="contact[company_name]"]',
      industry: 'select[name="contact[industry]"]',
      occasion: 'select[name="contact[occasion]"]',
      unitsQuantity: 'select[name="contact[units_quantity]"]',
      productInterested: 'input[name="contact[product_interested_checkbox][]"]',
      needCustomization: 'input[name="contact[need_customization]"]',
      estimatedBudget: 'input[name="contact[estimated_budget]"]',
      fulfillmentPlans: 'select[name="contact[fulfillment_plans]"]',
      targetDeliveryDate: 'input[name="contact[target_delivery_date]"]',
      referralSource: 'select[name="contact[referral_source]"]'
    };
    this.formFieldsSpanElements = {
      firstName: this.querySelector('span[name="contact[first_name]"]'),
      lastName: this.querySelector('span[name="contact[last_name]"]'),
      email: this.querySelector('span[name="contact[email]"]'),
      companyName: this.querySelector('span[name="contact[company_name]"]'),
      industry: this.querySelector('span[name="contact[industry]"]'),
      occasion: this.querySelector('span[name="contact[occasion]"]'),
      unitsQuantity: this.querySelector('span[name="contact[units_quantity]"]'),
      productInterested: this.querySelector('span[name="contact[product_interested_checkbox]"]'),
      needCustomization: this.querySelector('span[name="contact[need_customization]"]'),
      estimatedBudget: this.querySelector('span[name="contact[estimated_budget]"]'),
      fulfillmentPlans: this.querySelector('span[name="contact[fulfillment_plans]"]'),
      targetDeliveryDate: this.querySelector('span[name="contact[target_delivery_date]"]'),
      referralSource: this.querySelector('span[name="contact[referral_source]"]')
    };
    this.init();
  }

  init() {
    if (document.documentElement.classList.contains('corporate-gifting-form-popup-open')) {
      document.documentElement.classList.remove('corporate-gifting-form-popup-open');
      document.documentElement.classList.add('corporate-gifting-form-popup-closing');

      window.setTimeout(function() {
        document.documentElement.classList.remove('corporate-gifting-form-popup-closing');
      }.bind(this), 500);
    }

    this.openPopupButton.addEventListener('click', this.openPopup.bind(this));
    this.corporateGiftingFormPopupCloseButton.addEventListener('click', this.closePopup.bind(this));
    this.form.addEventListener('submit', this.submitForm.bind(this));
    // this.submitButton.addEventListener('click', this.submitForm.bind(this));
  }

  openPopup(event) {
    this.corporateGiftingFormPopup.classList.add('is-opened');
    document.documentElement.classList.add('corporate-gifting-form-popup-open');
  }

  closePopup(event) {
    this.corporateGiftingFormPopup.classList.remove('is-opened');
    document.documentElement.classList.remove('corporate-gifting-form-popup-open');
    document.documentElement.classList.add('corporate-gifting-form-popup-closing');

    window.setTimeout(function() {
      document.documentElement.classList.remove('corporate-gifting-form-popup-closing');
    }.bind(this), 500);
  }

  submitForm(event) {
    this.submitButton.disabled = true;
    if (!this.validateForm()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.submitButton.disabled = false;
    } else {
      // Format the product interested field so it could be submitted to the Shopify's backend
      const productInterestedCheckboxes = this.querySelectorAll(this.formFieldsSelector.productInterested);
      const productInterestedChecked = this.querySelectorAll('input[name="contact[product_interested_checkbox][]"]:checked');
      const productInterestedCheckedValues = Array.from(productInterestedChecked).map(function (checkbox) {
        return checkbox.value;
      });
      const productInterestedCheckedString = productInterestedCheckedValues.join(', ');

      // Save the formatted value to the product interested field that is sent to the backend
      const productInterested = this.querySelector('input[name="contact[product_interested]"]');
      productInterested.value = productInterestedCheckedString;

      // Disable the existing checkboxes so it won't be sent to the backend
      productInterestedCheckboxes.forEach(function (checkbox) {
        checkbox.disabled = true;
      });
    }
  }

  validateForm() {
    this.refreshAllFormFieldsValidation();

    var allFieldsOk = true;

    // Validate first name
    const firstName = this.querySelector(this.formFieldsSelector.firstName);
    if (firstName.value === '') {
      allFieldsOk = false;
      this.displayErrorField(firstName, this.formFieldsSpanElements.firstName, this.formErrorMessages.inputTextRequired);
    }

    // Validate last name
    const lastName = this.querySelector(this.formFieldsSelector.lastName);
    if (lastName.value === '') {
      allFieldsOk = false;
      this.displayErrorField(lastName, this.formFieldsSpanElements.lastName, this.formErrorMessages.inputTextRequired);
    }

    // Validate email
    const email = this.querySelector(this.formFieldsSelector.email);
    if (email.value === '') {
      allFieldsOk = false;
      this.displayErrorField(email, this.formFieldsSpanElements.email, this.formErrorMessages.inputTextRequired);
    }

    // Validate company name
    const companyName = this.querySelector(this.formFieldsSelector.companyName);
    if (companyName.value === '') {
      allFieldsOk = false;
      this.displayErrorField(companyName, this.formFieldsSpanElements.companyName, this.formErrorMessages.inputTextRequired);
    }

    // Validate industry
    const industry = this.querySelector(this.formFieldsSelector.industry);
    if (industry.value === '') {
      allFieldsOk = false;
      this.displayErrorField(industry, this.formFieldsSpanElements.industry, this.formErrorMessages.selectErrorRequired);
    }

    // Validate occasion
    const occasion = this.querySelector(this.formFieldsSelector.occasion);
    if (occasion.value === '') {
      allFieldsOk = false;
      this.displayErrorField(occasion, this.formFieldsSpanElements.occasion, this.formErrorMessages.selectErrorRequired);
    }

    // Validate units quantity
    const unitsQuantity = this.querySelector(this.formFieldsSelector.unitsQuantity);
    if (unitsQuantity.value === '') {
      allFieldsOk = false;
      this.displayErrorField(unitsQuantity, this.formFieldsSpanElements.unitsQuantity, this.formErrorMessages.selectErrorRequired);
    }

    // Validate product interested
    const productInterestedChecked = this.querySelectorAll('input[name="contact[product_interested_checkbox][]"]:checked');
    if (productInterestedChecked.length < 1) {
      allFieldsOk = false;
      this.formFieldsSpanElements.productInterested.innerText = this.formErrorMessages.inputCheckboxRequired;
      this.formFieldsSpanElements.productInterested.classList.add('error-display');
    }

    // Validate need customization
    const needCustomization = this.querySelector('input[name="contact[need_customization]"]:checked');
    if (!needCustomization) {
      allFieldsOk = false;
      this.formFieldsSpanElements.needCustomization.innerText = this.formErrorMessages.inputRadioRequired;
      this.formFieldsSpanElements.needCustomization.classList.add('error-display');
    }

    // Validate estimated budget
    const estimatedBudget = this.querySelector(this.formFieldsSelector.estimatedBudget);
    if (estimatedBudget.value === '') {
      allFieldsOk = false;
      this.displayErrorField(estimatedBudget, this.formFieldsSpanElements.estimatedBudget, this.formErrorMessages.inputTextRequired);
    }

    // Validate fulfillment plans
    const fulfillmentPlans = this.querySelector(this.formFieldsSelector.fulfillmentPlans);
    if (fulfillmentPlans.value === '') {
      allFieldsOk = false;
      this.displayErrorField(fulfillmentPlans, this.formFieldsSpanElements.fulfillmentPlans, this.formErrorMessages.selectErrorRequired);
    }

    // Validate target delivery date
    const targetDeliveryDate = this.querySelector(this.formFieldsSelector.targetDeliveryDate);
    if (targetDeliveryDate.value === '') {
      allFieldsOk = false;
      this.displayErrorField(targetDeliveryDate, this.formFieldsSpanElements.targetDeliveryDate, this.formErrorMessages.inputTextRequired);
    }

    // Validate referral source
    const referralSource = this.querySelector(this.formFieldsSelector.referralSource);
    if (referralSource.value === '') {
      allFieldsOk = false;
      this.displayErrorField(referralSource, this.formFieldsSpanElements.referralSource, this.formErrorMessages.selectErrorRequired);
    }

    return allFieldsOk;
  }

  displayErrorField(inputField, inputFieldErrorSpan, errorMessage) {
    inputField.classList.add('error-display');
    inputFieldErrorSpan.innerText = errorMessage;
    inputFieldErrorSpan.classList.add('error-display');
  }

  refreshAllFormFieldsValidation() {
    for (const key in this.formFieldsSelector) {
      if (key === 'productInterested' || key === 'needCustomization') {
        continue;
      }

      const field = this.querySelector(this.formFieldsSelector[key]);
      if (field.classList.contains('error-display')) {
        field.classList.remove('error-display');
      }
    }

    for (const key in this.formFieldsSpanElements) {
      const fieldSpan = this.formFieldsSpanElements[key];
      fieldSpan.innerText = '';
      if (fieldSpan.classList.contains('error-display')) {
        fieldSpan.classList.remove('error-display');
      }
    }
  }
}

customElements.define('corporate-gifting-form', CorporateGiftingForm);

/**
 * Collection comparison
 */
class CollectionComparison extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.sectionId = this.dataset.sectionId;
    this.dropdown = this.querySelector('select[name="collection-comparison-type-dropdown"]');
    this.dropdownWrapper = this.querySelector('.collection-comparison-type-dropdown');
    this.collectionTypes = [];
    this.grid = this.querySelector('.collection-comparison__grid');
    this.gridItems = this.querySelectorAll('.collection-comparison__grid-item--content');
    this.gridWrapper = this.querySelector('.collection-comparison__grid-wrapper');
    this.defaultCurrentHash = 'all_collections';
    this.currentHash = window.location.hash ? window.location.hash.slice(1) : this.defaultCurrentHash;
    this.init();
  }

  init() {
    this.initTypeDropdown();
    this.dropdown.addEventListener('change', this.handleTypeChange.bind(this));
    window.addEventListener('hashchange', this.handleHashChange.bind(this));
    this.loadCurrentHashContent();

    /**
     * Adjust refresh grid container
     * This is commented because we do it by custom css right now
     * Enable this if we want to set the collection name row size automatically
     */
    // this.refreshGridContainer();
    // window.addEventListener('resize', this.refreshGridContainer.bind(this));

    this.initDragScroll();
    this.initStickyRowHeight();
  }

  initTypeDropdown() {
    const createdDropdown = document.createElement('div');
    createdDropdown.setAttribute('class', 'collection-comparison-type-dropdown__selected');
    createdDropdown.innerHTML = this.dropdown.options[this.dropdown.selectedIndex].innerHTML;

    this.dropdownWrapper.appendChild(createdDropdown);

    const createdDropdownSelectItems = document.createElement('div');
    createdDropdownSelectItems.setAttribute('class', 'collection-comparison-type-dropdown__select-items collection-comparison-type-dropdown__select-items--hidden');
    for (var i = 0; i < this.dropdown.length; i++) {
      const createdDropdownSelectOptions = document.createElement('div');
      createdDropdownSelectOptions.innerHTML = this.dropdown.options[i].innerHTML;

      // Add to collection types array
      const dropdownValue = this.dropdown.options[i].value;
      this.collectionTypes.push(dropdownValue);

      if (i === 0) {
        createdDropdownSelectOptions.setAttribute('class', 'collection-comparison-type-dropdown__select-items-same-as-selected');
      }
      createdDropdownSelectOptions.dataset.type = dropdownValue;

      createdDropdownSelectOptions.addEventListener('click', function (e) {
        const selectDropdown = this.parentNode.parentNode.getElementsByTagName("select")[0];
        const selectedText = this.parentNode.previousSibling;

        for (var j = 0; j < selectDropdown.length; j++) {
          if (selectDropdown.options[j].innerHTML == this.innerHTML) {
            selectDropdown.selectedIndex = j;

            const changeEvent = new Event('change', { bubbles: true });
            selectDropdown.dispatchEvent(changeEvent);

            selectedText.innerHTML = this.innerHTML;
            const selectedDropdownOptions = this.parentNode.getElementsByClassName('collection-comparison-type-dropdown__select-items-same-as-selected');
            for (var k = 0; k < selectedDropdownOptions.length; k++) {
              selectedDropdownOptions[k].removeAttribute('class');
            }
            this.setAttribute('class', 'collection-comparison-type-dropdown__select-items-same-as-selected');
            break;
          }
        }
        selectedText.click();
      });
      createdDropdownSelectItems.appendChild(createdDropdownSelectOptions);
    }

    this.dropdownWrapper.appendChild(createdDropdownSelectItems);
    var self = this;
    createdDropdown.addEventListener('click', function (e) {
      e.stopPropagation();
      self.closeTypeSelectDropdown(this);
      this.nextSibling.classList.toggle('collection-comparison-type-dropdown__select-items--hidden');
      this.classList.toggle('collection-comparison-type-dropdown__selected--active');
    });

    document.addEventListener('click', function (e) {
      self.closeTypeSelectDropdown(this);
    });
  }

  closeTypeSelectDropdown(element) {
    const dropdownSelectItems = this.querySelectorAll('.collection-comparison-type-dropdown__select-items');
    const dropdownSelected = this.querySelectorAll('.collection-comparison-type-dropdown__selected');
    var selectedOptions = [];

    dropdownSelected.forEach(function (item, index) {
      if (element == item) {
        selectedOptions.push(index);
      } else {
        dropdownSelected[index].classList.remove('collection-comparison-type-dropdown__selected--active');
      }
    });

    dropdownSelectItems.forEach(function (item, index) {
      if (selectedOptions.indexOf(index)) {
        dropdownSelectItems[index].classList.add('collection-comparison-type-dropdown__select-items--hidden');
      }
    });
  }

  refreshGridContainer() {
    const dropdownContainer = this.querySelector('.collection-comparison__grid-item--header-dropdown');
    this.style.setProperty('--backgroundHeight-' + this.sectionId, dropdownContainer.offsetHeight + 'px');
  }

  handleTypeChange(event) {
    this.hideAllGridItems();
    const selectedType = event.target.value;

    // Change URL hash
    // window.location.hash = selectedType;
    history.replaceState(null, null, '#' + selectedType);

    const gridItemsNeedsToBeShown = this.querySelectorAll('.collection-comparison__grid-item--content[data-comparison-collection-type="' + selectedType + '"]');
    const gridItemCollectionColumn = this.querySelectorAll('.collection-comparison__grid-item--collection[data-comparison-collection-type="' + selectedType + '"]');
    gridItemsNeedsToBeShown.forEach(function (item) {
      if (item.classList.contains('collection-comparison__grid-item--hidden')) {
        item.classList.remove('collection-comparison__grid-item--hidden');
      }
    });
    this.grid.style.setProperty('--totalColumnsRendered-' + this.sectionId, gridItemCollectionColumn.length);
  }

  hideAllGridItems() {
    this.gridItems.forEach(function (item) {
      if (!item.classList.contains('collection-comparison__grid-item--hidden')) {
        item.classList.add('collection-comparison__grid-item--hidden');
      }
    });
  }

  loadCurrentHashContent() {
    console.log('run');

    var currentHash;
    if (this.collectionTypes.indexOf(this.currentHash) !== -1) {
      currentHash = this.currentHash;
    } else {
      currentHash = this.defaultCurrentHash;
    }

    const selectItemsOptionClicked = this.querySelector('.collection-comparison-type-dropdown__select-items div[data-type="' + currentHash + '"]');
    if (selectItemsOptionClicked) {
      selectItemsOptionClicked.click();
    }
  }

  handleHashChange(event) {
    this.currentHash = window.location.hash ? window.location.hash.slice(1) : this.defaultCurrentHash;
    this.loadCurrentHashContent();
  }

  /**
   * Size the scrolling table box to the viewport minus the sticky site header, so the
   * pinned collection row is never hidden behind it.
   *
   * The offset has to be measured: the header is only sticky on some pages, and its
   * height varies with the announcement bar and breakpoint. #StickyHeaderWrap is created
   * by theme.js only when the sticky header is actually enabled, so its absence means no
   * offset is needed. This runs on init, load and resize - never during scroll.
   */
  initStickyRowHeight() {
    this.stickyWrapper = this.querySelector('.collection-comparison__grid-wrapper--collection-row-sticky');
    if (!this.stickyWrapper) return;

    this.updateStickyRowHeight = this.updateStickyRowHeight.bind(this);
    this.updateStickyRowHeight();

    // theme.js may not have wrapped the header yet when this element is constructed.
    window.addEventListener('load', this.updateStickyRowHeight);
    window.addEventListener('resize', this.updateStickyRowHeight);
  }

  updateStickyRowHeight() {
    const stickyHeaderWrap = document.getElementById('StickyHeaderWrap');
    const siteHeader = stickyHeaderWrap && stickyHeaderWrap.querySelector('.site-header');
    // Measured while unstuck. The header loses padding once stuck, so erring on the taller
    // measurement leaves a small gap rather than letting it cover the pinned row.
    const offset = siteHeader ? Math.round(siteHeader.getBoundingClientRect().height) : 0;
    this.stickyWrapper.style.setProperty('--collectionComparisonHeaderOffset', offset + 'px');
  }

  initDragScroll() {
    const el = this.gridWrapper;
    let isDown = false;
    let startX;
    let scrollLeft;

    el.addEventListener('mousedown', function(e) {
      isDown = true;
      el.classList.add('is-dragging');
      startX = e.pageX - el.offsetLeft;
      scrollLeft = el.scrollLeft;
    });

    el.addEventListener('mouseleave', function() {
      isDown = false;
      el.classList.remove('is-dragging');
    });

    el.addEventListener('mouseup', function() {
      isDown = false;
      el.classList.remove('is-dragging');
    });

    el.addEventListener('mousemove', function(e) {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - el.offsetLeft;
      const walk = (x - startX) * 1.5;
      el.scrollLeft = scrollLeft - walk;
    });
  }
}

customElements.define('collection-comparison', CollectionComparison);

class ContentWithSidebar extends HTMLElement {
  constructor() {
    super();
    this.el = this;
    this.sectionId = this.dataset.sectionId;
    this.showNavigation = this.dataset.showNavigation === 'true' ? true : false;
    this.scrollSpyNavigation = this.dataset.scrollSpyNavigation === 'true' ? true : false;
    this.init();
  }

  init() {
    if (this.showNavigation) {
      this.initNavigation();
    }
    if (this.scrollSpyNavigation) {
      this.initScrollSpyNavigation();
    }
  }

  initNavigation() {
    const navigationItems = this.querySelectorAll('.content-with-sidebar__navigation-item');
    const self = this;
    navigationItems.forEach(function (item) {
      item.addEventListener('click', self.handleNavigationItemClick.bind(self));
    });
  }

  handleNavigationItemClick(event) {
    this.refreshNavigationItemStatuses();

    const clickedElement = event.target;
    clickedElement.classList.add('content-with-sidebar__navigation-item--active');

    const blockId = clickedElement.dataset.blockId;
    const dotElement = this.querySelector('.content-with-sidebar__navigation-dot[data-block-id="' + blockId + '"]');
    if (dotElement) {
      dotElement.classList.add('content-with-sidebar__navigation-dot--active');
    }
  }

  refreshNavigationItemStatuses() {
    const navigationItems = this.querySelectorAll('.content-with-sidebar__navigation-item');
    const navigationDotItems = this.querySelectorAll('.content-with-sidebar__navigation-dot');
    
    navigationItems.forEach(function (item) {
      if (item.classList.contains('content-with-sidebar__navigation-item--active')) {
        item.classList.remove('content-with-sidebar__navigation-item--active')
      }
    });

    navigationDotItems.forEach(function (item) {
      if (item.classList.contains('content-with-sidebar__navigation-dot--active')) {
        item.classList.remove('content-with-sidebar__navigation-dot--active');
      }
    });
  }

  initScrollSpyNavigation() {
    const self = this;
    const options = {
      threshold: 0,
      rootMargin: '0px 0px -70% 0px',
    };

    // Scroll spy handle
    const scrollSpyHandle = function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          self.refreshNavigationItemStatuses();

          const intersectingElement = entry.target;
          const itemBlockId = intersectingElement.dataset.blockId;
          if (itemBlockId) {
            const intersectingMenuElement = self.querySelector('.content-with-sidebar__navigation-item[data-block-id="' + itemBlockId + '"]');
            if (intersectingMenuElement) {
              intersectingMenuElement.classList.add('content-with-sidebar__navigation-item--active');

              const intersectingMenuElementDot = self.querySelector('.content-with-sidebar__navigation-dot[data-block-id=' + itemBlockId + ']');
              if (intersectingMenuElementDot) {
                intersectingMenuElementDot.classList.add('content-with-sidebar__navigation-dot--active');
              }
            }
          }
        }
      });
    };

    const observer = new IntersectionObserver(scrollSpyHandle, options);
    const contentHeadings = this.querySelectorAll('.content-with-sidebar__heading[data-is-navigation="true"]');
    contentHeadings.forEach(function (contentHeading) {
      observer.observe(contentHeading);
    });

  }
}

customElements.define('content-with-sidebar', ContentWithSidebar);


/**
 * Hero video sound toggle
 *
 * The hero video autoplays, which browsers only allow while it is muted, so it always
 * starts silent and this checkbox is what turns sound on. Delegated from the document so
 * it covers every hero-video section on the page without per-section wiring.
 */
document.addEventListener('change', function (event) {
  var checkbox = event.target;
  if (!checkbox.classList || !checkbox.classList.contains('hero__sound-checkbox')) return;

  var video = document.getElementById(checkbox.dataset.heroSoundFor);
  if (!video) return;

  video.muted = !checkbox.checked;

  // Unmuting counts as a user gesture, so a video that was refused autoplay earlier can
  // start here. Nothing to do if it is already playing, and a rejection is not an error.
  if (!video.muted) {
    var playPromise = video.play();
    if (playPromise !== undefined) playPromise.catch(function () {});
  }
});

/**
 * Background video performance
 *
 * theme.VideoSection starts a video then unobserves it, so nothing ever pauses
 * it again and every video keeps decoding off-screen. This watches them
 * instead: pause when out of view, resume when back.
 *
 * Pausing a video mid-start rejects the play() promise the theme is waiting on,
 * and its catch handler answers that by showing native controls. The margin and
 * the `starting` set below both exist to avoid that.
 *
 * Observes in place, never wraps: an extra element resizes the hero media box.
 * Matches <video> only; YouTube and Vimeo render as <div class="video-div">.
 * Posters are set in Liquid so they reach the initial HTML.
 */
class BackgroundVideoPerformance {
  constructor() {
    this.selector = 'video.video-div';

    // Wider than theme.VideoSection's own 500px start threshold, so the theme
    // never starts a video this controller counts as out of view
    this.margin = 600;

    // WeakSets throughout: entries vanish when an element leaves the page
    this.pausedByController = new WeakSet(); // paused by us, so only we resume
    this.registered = new WeakSet();         // already observed, no double listeners
    this.starting = new WeakSet();           // mid-start, unsafe to pause

    this.observer = null;
  }

  init() {
    // In the theme editor a section can gain a video at any time
    const inThemeEditor = !!(window.Shopify && window.Shopify.designMode);
    if (!document.querySelector(this.selector) && !inThemeEditor) {
      return;
    }

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => this.handleIntersect(entry)),
      { rootMargin: this.margin + 'px 0px ' + this.margin + 'px 0px', threshold: 0 }
    );

    this.register(document);

    document.addEventListener('visibilitychange', () => this.handleVisibilityChange());

    // Editing a section re-renders it, replacing its video elements
    document.addEventListener('shopify:section:load', (event) => this.register(event.target));
    document.addEventListener('shopify:section:unload', (event) => this.unregister(event.target));
  }

  register(root) {
    root.querySelectorAll(this.selector).forEach((video) => {
      if (this.registered.has(video)) {
        return;
      }
      this.registered.add(video);

      this.observer.observe(video);

      // The observer only fires on a state change, so playback is tracked here
      video.addEventListener('play', () => this.starting.add(video));
      video.addEventListener('playing', () => {
        this.starting.delete(video);
        this.handlePlaying(video);
      });
    });
  }

  unregister(root) {
    root.querySelectorAll(this.selector).forEach((video) => {
      this.observer.unobserve(video);
      this.registered.delete(video);
    });
  }

  handlePlaying(video) {
    if (!this.isWithinMargin(video)) {
      this.suspend(video);
    }
  }

  // Measured directly, so it holds before the observer first reports
  isWithinMargin(video) {
    const rect = video.getBoundingClientRect();
    return rect.top < window.innerHeight + this.margin && rect.bottom > -this.margin;
  }

  handleIntersect(entry) {
    if (entry.isIntersecting) {
      this.resume(entry.target);
    } else {
      this.suspend(entry.target);
    }
  }

  handleVisibilityChange() {
    // Queried fresh, so no element is held after it leaves the page
    document.querySelectorAll(this.selector).forEach((video) => {
      if (document.hidden) {
        this.suspend(video);
      } else {
        this.resume(video);
      }
    });
  }

  suspend(video) {
    if (video.paused) {
      return;
    }

    // handlePlaying pauses it instead, once its play() promise has settled
    if (this.starting.has(video)) {
      return;
    }

    video.pause();
    this.pausedByController.add(video);
  }

  resume(video) {
    // Leave alone a visitor pause, or a video the theme has not started yet
    if (!this.pausedByController.has(video) || document.hidden) {
      return;
    }

    // Returning to the tab resumes everything paused, most of it off-screen
    if (!this.isWithinMargin(video)) {
      return;
    }

    this.pausedByController.delete(video);

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch(function () {
        // Autoplay can be refused, for example in low power mode
      });
    }
  }
}

// Deferred script, so the document is already parsed here
const backgroundVideoPerformance = new BackgroundVideoPerformance();

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', function () {
    backgroundVideoPerformance.init();
  });
} else {
  backgroundVideoPerformance.init();
}
